import BreadCrumbs from 'components/breadcrumbs'
import { useAbilityAccess } from 'hooks/useAbilityAccess'
import moment from 'moment-timezone'
import ModalDrafts from 'private-views/transactions/ModalDrafts'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'
import {
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap'
import CustomButton from 'components/Buttons/CustomButton'
import LoadingAnimation from 'components/LoadingAnimation'
import { formatInputNumber } from 'utility/Utils'
import { URL_406 } from 'utils/pathUrl'
import { BUSINESS_TRANSACTION_SELL, CREATE, MAKE } from 'utils/subjectActions'
import { useFetchSettingsQuery } from '../settings/settingApiSlice'
import InfoDocument from './InfoDoc'
import ItemInfo from './InfoItem'
import InfoSeller from './InfoSeller'
import InfoTotalAmount from './InfoTotalAmount'
import ModalInfo from './ModalTrx'
import {
  checkSellerInfo,
  getInventories,
  getPackings,
} from './sellCommodityAction'
import {
  resetSellCommodityState,
  setInvPacking,
  setItemCount,
  setOrders,
  setOrderSeller,
  setProject,
  toggleModalDraft,
} from './sellCommoditySlice'

const SellCommodityView = () => {
  const { isLoading, isSuccess, data } = useFetchSettingsQuery()
  const dispatch = useDispatch()

  const [screenWidth, setScreenWidth] = useState(window.innerWidth)
  const [isInventory, setIsInventory] = useState([false])
  const [fileNames, setFileNames] = useState([])
  const [isScannerPrefillLoading, setIsScannerPrefillLoading] = useState(false)
  const { profile } = useSelector((state) => state.dashboard)
  const {
    addItemCount,
    orders,
    isOpenModalDraft,
    invPacking,
    comodities,
    inventories,
    packings,
  } = useSelector((state) => state.sellCommodity)

  const [showMissingInvModal, setShowMissingInvModal] = useState(false)
  const hasCheckedMissingInv = useRef(false)

  const location = useLocation()

  // Store prefill data from "Ulangi Transaksi" to use when user clicks "Hapus"
  const prefillDataRef = useRef(null)
  // Store draft data snapshot when modal is shown to prevent overwriting
  const draftDataRef = useRef(null)
  // Suppress autosave when arriving from "Ulangi Pesanan" and waiting for user
  // decision so existing draft is not overwritten.
  const suppressAutoSaveRef = useRef(false)

  const createSell = useAbilityAccess({
    action: CREATE,
    subject: BUSINESS_TRANSACTION_SELL,
  })
  const makeSell = useAbilityAccess({
    action: MAKE,
    subject: BUSINESS_TRANSACTION_SELL,
  })

  const onConfirm = async () => {
    // Use snapshot of draft data taken when modal appeared
    // This prevents data from being overwritten by auto-save effect
    const getUnifiedDraft = (userId) => {
      const key = `userData-${userId}`
      const raw = localStorage.getItem(key)
      if (raw && raw !== 'null') return JSON.parse(raw)
      return null
    }

    const raw = draftDataRef.current || getUnifiedDraft(profile?.userId)
    const buildOrdersFromLegacy = (d) => {
      if (!d) return null
      if (d.orders) return d.orders
      if (d.dataComodities && Array.isArray(d.dataComodities)) {
        const parseNumber = (v) => {
          if (v === null || v === undefined || v === '') return 0
          if (typeof v === 'number') return v
          const s = String(v).trim()
          const cleaned = s.replace(/\./g, '').replace(/,/g, '.')
          return Number(cleaned) || 0
        }

        const items = d.dataComodities.map((it, i) => {
          const numericPrice = parseNumber(it?.price ?? 0)
          const numericTotal = parseNumber(it?.totalPrice ?? 0)

          return {
            orderItemId: i + 1,
            SKU: it?.comodity?.sku || '',
            itemId: it?.comodity?.id ?? it?.comodity?.value ?? null,
            amount: it?.quantity ?? it?.amount ?? '',
            imageUrl: it?.comodity?.img || '',
            nameItem: it?.comodity?.label || it?.comodity?.name || '',
            price: numericPrice,
            totalPrice: numericTotal,
            unit: it?.unit || { label: 'Kg', value: 'Kg' },
          }
        })

        return {
          isLoading: false,
          step: 0,
          userId: profile?.userId || null,
          item: items,
          order: { type: null, notes: d.notes || '', transaction_number: '' },
          buyer: null,
          seller: d.dataSeller || null,
          trxDate: d.trxDate || moment(new Date()).format('YYYY-MM-DD'),
          project: null,
        }
      }
      return null
    }

    const data = raw
    const ordersToUse = data?.orders ?? buildOrdersFromLegacy(data)
    if (data?.dataComodities) {
      // applyPrefillData will dispatch setOrders / setItemCount and perform splits
      await applyPrefillData({
        prefillOrders: ordersToUse,
        prefillAddItemCount: data?.addItemCount,
        shouldFetchSeller: false,
      })
    } else {
      dispatch(setOrders(ordersToUse))
      dispatch(setItemCount(data?.addItemCount || [1]))
    }
    dispatch(setInvPacking(data?.invPacking))
    setIsInventory(data?.isInventory || [false])
    const restoredFileNames = (data?.fileNames || []).map((file, idx) => ({
      ...file,
      url: file.url || ordersToUse?.order?.files?.[idx] || null,
    }))
    setFileNames(restoredFileNames)
    // Clear refs - user chose to use draft
    prefillDataRef.current = null
    draftDataRef.current = null
    // re-enable autosave
    suppressAutoSaveRef.current = false
    window.history.replaceState({}, document.title)
    dispatch(toggleModalDraft())
  }

  const applyPrefillData = async (prefillData) => {
    if (!prefillData || !prefillData.prefillOrders) return

    const {
      prefillOrders,
      prefillAddItemCount,
      shouldFetchSeller,
      sellerPhoneNumber,
    } = prefillData

    // Build normalized items and split inventory entries when the
    // available inventory amount is larger than the requested amount.
    const parseNumber = (v) => {
      if (v === null || v === undefined || v === '') return 0
      if (typeof v === 'number') return v
      const s = String(v).trim()
      const cleaned = s.replace(/\./g, '').replace(/,/g, '.')
      return Number(cleaned) || 0
    }

    const processedItems = []
    for (const it of prefillOrders.item || []) {
      const unitStr = (it?.unit?.value ?? it?.unit ?? '')
        ?.toString()
        ?.toLowerCase()

      let numericAmount = parseNumber(it?.amount ?? 0)

      const numericPrice = parseNumber(it?.price ?? 0)

      // determine available inventory amount from various possible fields
      const available = parseNumber(
        it?.originalAmount ?? it?.itemDetail?.amount ?? it?.item?.amount ?? 0,
      )

      // Default behavior: do not perform automatic split here — keep original item
      processedItems.push({
        ...it,
        amount: formatInputNumber(numericAmount),
        price: numericPrice,
        totalPrice: numericAmount * numericPrice,
        isPacking:
          it.isFromManageStock ||
          it.isPacking ||
          !!(it.inventoryId || it.itemDetail?.inventoryId),
      })
    }

    // reassign sequential orderItemId
    const itemsWithIds = processedItems.map((it, idx) => ({
      ...it,
      orderItemId: idx + 1,
    }))

    const normalizedOrder = {
      type: prefillOrders?.order?.type || prefillOrders?.type || 'SELL',
      notes: prefillOrders?.order?.notes ?? '',
      transaction_number: prefillOrders?.order?.transaction_number ?? '',
      files: prefillOrders?.order?.files ?? [],
      ...(prefillOrders?.order || {}),
    }

    const fallbackSeller = profile?.userId
      ? {
          fullName: profile?.fullName || profile?.name || '',
          phoneNumber: profile?.phoneNumber || '',
          email: profile?.email || '',
          userId: profile?.userId,
        }
      : null

    const normalized = {
      ...orders,
      ...prefillOrders,
      userId:
        prefillOrders?.userId ?? orders?.userId ?? profile?.userId ?? null,
      seller: prefillOrders?.seller ?? orders?.seller ?? fallbackSeller,
      buyer: prefillOrders?.buyer ?? orders?.buyer ?? null,
      order: normalizedOrder,
      item: itemsWithIds,
    }

    if (Object.prototype.hasOwnProperty.call(normalized, 'type')) {
      delete normalized.type
    }

    dispatch(setOrders(normalized))

    if (prefillOrders.project) {
      dispatch(setProject(prefillOrders.project))
    }

    if (prefillOrders.seller) {
      dispatch(setOrderSeller(prefillOrders.seller))
    }

    // detect if items are from inventory (after processing/splitting)
    const inventoryFlags = (itemsWithIds || []).map(
      (it) => !!(it?.inventoryId || it?.itemDetail?.inventoryId),
    )
    setIsInventory(inventoryFlags.length > 0 ? inventoryFlags : [false])

    // ensure addItemCount matches processed items (after splitting)
    const newAddItemCount = (itemsWithIds || []).map((it) => it.orderItemId)
    dispatch(setItemCount(newAddItemCount))

    // If requested, fetch seller info so orders.seller (including bank fields)
    // is populated in the sell slice before user proceeds. After checkSellerInfo
    // resolves, explicitly set `orders.seller` using the slice action.
    if (shouldFetchSeller && sellerPhoneNumber) {
      try {
        const res = await dispatch(
          checkSellerInfo({
            search: encodeURIComponent(sellerPhoneNumber),
            type: 'SELL',
          }),
        ).unwrap()
        // ensure seller is set on orders (includes bankAccountNumber)
        // dispatch(setOrderSeller(res))
      } catch (e) {
        // ignore fetch error here; user can still edit seller manually
      }
    }

    return normalized
  }

  const onRemove = async () => {
    if (prefillDataRef.current) {
      // applyPrefillData is async and will dispatch setOrders/setItemCount
      // await it to ensure splitting & seller-fetch complete before user continues
      await applyPrefillData(prefillDataRef.current)

      // Clear prefill data after use
      prefillDataRef.current = null
      window.history.replaceState({}, document.title)
      suppressAutoSaveRef.current = false
      dispatch(toggleModalDraft())
      return
    }

    // Not from prefill: delete draft
    localStorage.removeItem(`userData-${profile?.userId}`)
    localStorage.removeItem(`userDataSell-${profile?.userId}`)
    suppressAutoSaveRef.current = false
    dispatch(toggleModalDraft())
  }

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [dispatch])

  useEffect(() => {
    return () => {
      dispatch(resetSellCommodityState())
    }
  }, [dispatch])

  // Autosave removed: do NOT persist form state automatically to localStorage
  // to avoid overwriting an existing draft. Persistence happens only when the
  // user explicitly chooses to remove the draft (Hapus) from the draft modal.

  useEffect(() => {
    if (profile?.userId) {
      const getUnifiedDraft = (userId) => {
        const key = `userData-${userId}`
        const raw = localStorage.getItem(key)
        if (raw && raw !== 'null') return JSON.parse(raw)
        return null
      }

      const isDraftPresent = (d) => {
        if (!d) return false
        try {
          // legacy format: dataComodities (used by some flows)
          if (
            d.dataComodities &&
            Array.isArray(d.dataComodities) &&
            d.dataComodities.length > 0
          )
            return true
          const orders = d.orders || {}
          const items = orders.item || []
          if (items.length > 0) {
            for (const it of items) {
              const amt = Number(it?.amount ?? it?.quantity ?? 0)
              if (!Number.isNaN(amt) && Number(amt) > 0) return true
            }
          }
          if (d.invPacking && d.invPacking.length > 0) return true
          if (d.addItemCount && d.addItemCount.length > 0) return true
        } catch (e) {
          return false
        }
        return false
      }

      const data = getUnifiedDraft(profile?.userId)
      const hasDraft = isDraftPresent(data)
      const isScannerPrefill = location?.state?.prefillSource === 'scanner'

      // Store prefill data from "Ulangi Pesanan" to use when user clicks "Hapus"
      if (location?.state?.prefillOrders) {
        prefillDataRef.current = {
          prefillOrders: location.state.prefillOrders,
          prefillAddItemCount: location.state.prefillAddItemCount,
          shouldFetchSeller: location.state.shouldFetchSeller,
          sellerPhoneNumber: location.state.sellerPhoneNumber,
          prefillSource: location.state.prefillSource,
        }
      }

      // Show draft modal if draft exists
      if (hasDraft && !isScannerPrefill) {
        // IMPORTANT: Snapshot draft data RIGHT NOW before anything can change it
        draftDataRef.current = data
        // suppress autosave to avoid overwriting the draft while modal is up
        if (location?.state?.prefillOrders) suppressAutoSaveRef.current = true
        dispatch(toggleModalDraft())
      } else if (location?.state?.prefillOrders) {
        // No draft but we have prefill data from "Ulangi Pesanan"
        // Apply prefill data directly and await completion to avoid races
        void (async () => {
          const isScannerPrefillFlow =
            location?.state?.prefillSource === 'scanner'

          try {
            if (isScannerPrefillFlow) {
              setIsScannerPrefillLoading(true)
              // Ensure latest inventory sources are loaded before binding QR prefill.
              await Promise.allSettled([
                dispatch(getInventories()).unwrap(),
                dispatch(getPackings()).unwrap(),
              ])
            }

            await applyPrefillData({
              prefillOrders: location.state.prefillOrders,
              prefillAddItemCount: location.state.prefillAddItemCount,
              shouldFetchSeller: location.state.shouldFetchSeller,
              sellerPhoneNumber: location.state.sellerPhoneNumber,
            })
            // Clear prefill data after use
            prefillDataRef.current = null
            window.history.replaceState({}, document.title)
          } catch (e) {
            console.warn('apply prefill failed', e)
          } finally {
            if (isScannerPrefillFlow) {
              setIsScannerPrefillLoading(false)
            }
          }
        })()
      }
    }
  }, [
    profile?.userId,
    location?.state?.prefillOrders,
    location?.state?.prefillSource,
    dispatch,
  ])

  useEffect(() => {
    // Only check once, and only if we came from "Ulangi Pesanan"
    if (hasCheckedMissingInv.current || !location?.state?.prefillOrders) return

    // Scanner flow has its own grouped inventory resolution and modal handling.
    if (location?.state?.prefillSource === 'scanner') return

    // Wait until data is loaded
    if (comodities && inventories && packings) {
      hasCheckedMissingInv.current = true

      let hasMissingInventory = false
      const allInventories = [...(inventories || []), ...(packings || [])]

      // We check the original orders from location state since orders.item might
      // be in flux, but the prefill structure has inventoryId.
      const rawPrefillItems = location.state.prefillOrders.item || []

      for (const item of rawPrefillItems) {
        if (item?.isFromManageStock) {
          // Items coming directly from Manage Stock page definitely exist in inventory.
          continue
        }

        const isGroupedInventory = !!(
          item?.groupId || item?.itemDetail?.groupId
        )
        if (isGroupedInventory) {
          // Grouped inventory may not appear in outer inventory list.
          continue
        }

        const invId = item.originalAmount
          ? item.itemId || item.id
          : item.inventoryId || item.itemDetail?.inventoryId
        if (invId) {
          const found = allInventories.find((inv) => inv.id === invId)
          if (!found && item.seller) {
            // Usually inventory items from previous transactions have inventoryId.
            // If it's pure inventory, it might not be found.
          }
          // The previous auto-select searches by inventoryId.
          if (item.inventoryId) {
            const foundInv = allInventories.find(
              (inv) => inv.id === item.inventoryId,
            )
            if (!foundInv) {
              hasMissingInventory = true
              break
            }
          }
        }
      }

      if (hasMissingInventory) {
        setShowMissingInvModal(true)
      }
    }
  }, [
    comodities,
    inventories,
    packings,
    location?.state?.prefillOrders,
    location?.state?.prefillSource,
  ])

  const onLanjutMissingInv = () => {
    setShowMissingInvModal(false)
  }

  const onHapusMissingInv = () => {
    setShowMissingInvModal(false)
    dispatch(
      setOrders({
        isLoading: false,
        step: 0,
        userId: profile?.userId || null,
        item: [
          {
            orderItemId: 1,
            SKU: '',
            itemId: '',
            amount: '',
            imageUrl: '',
            nameItem: '',
            price: '',
            totalPrice: '',
            unit: { label: 'Kg', value: 'Kg' },
          },
        ],
        order: {
          type: null,
          notes: '',
          transaction_number: '',
        },
        buyer: null,
        seller: null,
        trxDate: moment(new Date()).format('YYYY-MM-DD'),
        project: null,
      }),
    )
    dispatch(setItemCount([1]))
    dispatch(setOrderSeller(null))
    dispatch(setProject(null))
    setIsInventory([false])
    setFileNames([])
    dispatch(setInvPacking([]))
    window.history.replaceState({}, document.title)
  }

  if (createSell || makeSell) {
    return (
      <div className="w-100">
        <ModalDrafts
          isOpen={isOpenModalDraft}
          toggle={() => dispatch(toggleModalDraft())}
          onConfirm={onConfirm}
          onRemove={onRemove}
        />
        <Modal
          isOpen={showMissingInvModal}
          toggle={() => {}}
          centered
          backdrop="static"
          keyboard={false}
        >
          <ModalHeader>Inventori Tidak Ditemukan</ModalHeader>
          <ModalBody>
            Beberapa barang inventori dari pesanan sebelumnya sudah tidak
            tersedia. Apakah Anda ingin melanjutkan dengan data yang ada, atau
            menghapus seluruh data dan mengulang dari awal?
          </ModalBody>
          <ModalFooter>
            <CustomButton color="danger" outline onClick={onHapusMissingInv}>
              Hapus
            </CustomButton>
            <CustomButton color="primary" onClick={onLanjutMissingInv}>
              Lanjut
            </CustomButton>
          </ModalFooter>
        </Modal>
        <Container className="custom-container">
          <div className="d-flex align-items-start gap-2 pt-3">
            <BreadCrumbs breadCrumbActive={'Penjualan'} />
          </div>
          <InfoSeller />
          <div className="my-3"></div>
          <ItemInfo isInventory={isInventory} setIsInventory={setIsInventory} />
          <div className="my-3"></div>
          <InfoDocument fileNames={fileNames} setFileNames={setFileNames} />
          <div style={{ paddingBottom: 100 }}></div>
        </Container>

        <InfoTotalAmount
          isLoading={isLoading}
          isSuccess={isSuccess}
          data={data}
        />
        <ModalInfo isManual={location.state?.isManual} />

        {isScannerPrefillLoading && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
              backdropFilter: 'blur(2px)',
            }}
          >
            <LoadingAnimation color="primary" />
            <h6 className="mt-3 fw-semibold text-primary">
              Menyiapkan inventori dari QR...
            </h6>
          </div>
        )}
      </div>
    )
  }
  return <Navigate to={URL_406} replace={true} />
}

export default SellCommodityView
